import api from "../axios/index";
import ai from "../axios/ai";
import axios from "axios";
import { BASE_URL_ML } from "../../utils/constants";
import { handleApiError } from "../../utils/handleApiError";

const getAccountId = () => {
  return sessionStorage.getItem("acc_id");
};

const getSessionId = () => {
  return sessionStorage.getItem("sessionId");
};

const getPatientId = () => {
  return sessionStorage.getItem("_pt_id");
};

const getCareplanId = () => {
  return sessionStorage.getItem("careplan_id");
};

const getLocationId = () => {
  return JSON.parse(sessionStorage.getItem("current_loc"))?.id;
};

const getSchemaName = () => {
  return sessionStorage.getItem("schema_name");
};

export const getCarePlan = async (month, year) => {
  const accountId = getAccountId();
  const locationId = getLocationId();
  const patientId = getPatientId();

  const response = await api.get(`/ccm/${accountId}/${locationId}/careplans/`, {
    params: {
      month,
      year,
      patient_id: patientId,
    },
  });
  return response.data;
};

export const getAiCarePlan = async (session_id, include_recording = true) => {
  const patientId = getPatientId();
  const schemaName = getSchemaName();
  try {
    const response = await axios.get(`${BASE_URL_ML}/chatbot/generate-notes`, {
      params: {
        db_schema: schemaName,
        session_id: session_id,
        patient_type: "CCM",
        patient_id: patientId,
        include_recording: include_recording,
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const addCarePlan = async (location_id, data) => {
  const accountId = getAccountId();
  const response = await api.post(
    `/ccm/${accountId}/${location_id}/careplans/`,
    data
  );
  return response.data;
};

export const deleteCarePlan = async () => {
  const accountId = getAccountId();
  const locationId = getLocationId();
  const careplanId = getCareplanId();
  const response = await api.delete(
    `/ccm/${accountId}/${locationId}/careplans/${careplanId}/`,
  );
  return response.data;
};

export const getCopilotCareplan = async (patientType) => {
  const sessionId = getSessionId();
  const response = await ai.get(`/chatbot/generate-notes`, {
    data: new FormData(),
    params: {
      session_id: sessionId,
      patient_type: patientType,
    },
  });
  return response.data.notes;
};

export const refineNote = async ({ text, communication_outcome }) => {
  const response = await ai.post(
    `/chatbot/refine-note`,
    { text, communication_outcome },
    { timeout: 30000 }
  );
  return response.data?.refined_text ?? "";
};

// Inline note autocomplete (Gmail-style ghost text), "replace the last word"
// contract: the model returns everything after the LAST WHITESPACE of `text`,
// i.e. the user's partial word repeated exactly as typed plus the
// continuation ("...assig" -> "assigned another medication"; trailing space
// -> bare continuation). The client validates the echoed token and appends
// only the new part (see computeRemainder in ui/note-autocomplete).
// `config` lets callers pass an AbortController `signal` to cancel stale requests.
export const autocompleteNote = async (text, config = {}) => {
  const response = await ai.post(
    `/chatbot/autocomplete`,
    { text },
    { timeout: 8000, ...config }
  );
  return response.data?.suggestion ?? "";
};

export const addCarePlanReview = async (careplanId, reviewData) => {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  const response = await api.post(
    `/ccm/${tenantId}/${patientId}/careplans/${careplanId}/reviews/`,
    reviewData
  );
  return response.data;
};

export const getCarePlanReviews = async (careplanId) => {
  const tenantId = getAccountId();
  const patientId = getPatientId();

  const response = await api.get(
    `/ccm/${tenantId}/${patientId}/careplans/${careplanId}/reviews/`
  );
  return response.data;
};

// Health Summary 12-month history for one metric (weight, blood_glucose,
// bmi, blood_pressure, height): the 12 months before the given care plan
// month, newest first, one row per month (value null when nothing recorded).
export const getHealthSummaryHistory = async ({ metric, month, year }) => {
  const accountId = getAccountId();
  const locationId = getLocationId();
  const patientId = getPatientId();

  const response = await api.get(
    `/ccm/${accountId}/${locationId}/careplans/health-summary-history/`,
    {
      params: {
        patient_id: patientId,
        metric,
        month: `${String(month).padStart(2, "0")}-${year}`,
      },
    }
  );
  return response.data;
};

export const getYearlyDocumentStatus = async (year) => {
  const accountId = getAccountId();
  const locationId = getLocationId();
  const patientId = getPatientId();

  const response = await api.get(
    `/ccm/${accountId}/${locationId}/careplans/yearly-document-status/`,
    {
      params: {
        patient_id: patientId,
        year,
      },
    }
  );
  return response.data;
};

// New: update an existing careplan by its ID (used for historical/previous-month edits)
export const updateHistoricalCarePlan = async (tenantId, locationId, careplanId, data) => {
  const token = localStorage.getItem("authToken");
  const response = await api.patch(
    `/ccm/${tenantId}/${locationId}/careplans/${careplanId}/`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};